import { CommandError, CommondResultError } from './errors'
import socketClient from './socketClient'
import diagnosticsManager from './diagnostics'

const DEFAULT_TIMEOUT = 15000

/**
 * 生成请求 ID，优先使用浏览器原生 uuid
 */
function generateRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function isFunction(fn) {
  return typeof fn === 'function'
}

/**
 * 消息枢纽：负责信封解析、命令-响应映射与事件广播
 */
class MessageHub {
  constructor(options = {}) {
    const { socket, diagnostics, commandTimeout = DEFAULT_TIMEOUT, clientId = 'web', autoConnect = false, meta = {}, resendOnReconnect = true } = options

    this.socket = socket
    this.diagnostics = diagnostics
    this.commandTimeout = commandTimeout
    this.clientId = clientId
    this.baseMeta = { ...meta }
    this.autoConnect = autoConnect
    this.resendOnReconnect = resendOnReconnect

    this.pendingCommands = new Map()
    this.eventHandlers = new Map()
    this.rawHandlers = new Set()

    this._handleSocketMessage = this._handleSocketMessage.bind(this)
    this._handleSocketClose = this._handleSocketClose.bind(this)
    this._handleSocketReconnect = this._handleSocketReconnect.bind(this)

    this._unsubscribes = [this.socket.onMessage(this._handleSocketMessage), this.socket.onClose(this._handleSocketClose), this.socket.onReconnect(this._handleSocketReconnect)]

    if (this.autoConnect) {
      this.socket.connect().catch((error) => {
        this.diagnostics.error('autoConnect.failed', { error })
      })
    }
  }

  destroy(options = {}) {
    const { closeSocket = true, reason = 'destroy' } = options

    if (Array.isArray(this._unsubscribes)) {
      this._unsubscribes.forEach((off) => {
        try {
          if (typeof off === 'function') {
            off()
          }
        } catch (error) {
          this.diagnostics.error('destroy.unsubscribeFailed', { error })
        }
      })
      this._unsubscribes = []
    }

    this.pendingCommands.forEach((context, requestId) => {
      this._finalizeCommand(requestId)
      if (typeof context.reject === 'function') {
        context.reject(
          new CommandError({
            message: '消息通道已销毁',
            code: 'messageHub.destroyed',
            action: context.action,
            requestId: context.requestId,
            data: context.payload,
            raw: context.envelope
          })
        )
      }
    })
    this.pendingCommands.clear()

    this.eventHandlers.forEach((set) => set.clear())
    this.eventHandlers.clear()

    this.rawHandlers.clear()

    if (closeSocket && this.socket && isFunction(this.socket.close)) {
      try {
        this.socket.close({ disableReconnect: true, reason })
      } catch (error) {
        this.diagnostics.error('destroy.closeSocketFailed', { error })
      }
    }
  }

  setMeta(meta) {
    this.baseMeta = { ...meta }
  }

  setClientId(clientId) {
    this.clientId = clientId
  }

  setCommandTimeout(timeout) {
    this.commandTimeout = timeout
  }

  setAutoConnect(autoConnect) {
    this.autoConnect = Boolean(autoConnect)
  }

  setResendOnReconnect(enabled) {
    this.resendOnReconnect = Boolean(enabled)
  }

  onEvent(action, handler) {
    if (!isFunction(handler)) return () => {}
    if (!this.eventHandlers.has(action)) {
      this.eventHandlers.set(action, new Set())
    }
    const set = this.eventHandlers.get(action)
    set.add(handler)
    return () => this.offEvent(action, handler)
  }

  offEvent(action, handler) {
    const set = this.eventHandlers.get(action)
    if (set) {
      set.delete(handler)
      if (set.size === 0) {
        this.eventHandlers.delete(action)
      }
    }
  }

  onRaw(handler) {
    if (!isFunction(handler)) return () => {}
    this.rawHandlers.add(handler)
    return () => this.offRaw(handler)
  }

  offRaw(handler) {
    this.rawHandlers.delete(handler)
  }

  /**
   * 发送事件：自动挂载元信息，不返回 Promise（事件是单向的）
   */
  sendEvent(action, payload = {}, options = {}) {
    const { meta } = options

    if (this.autoConnect && !this.socket.isConnected()) {
      this.socket.connect().catch((error) => {
        this.diagnostics.error('connect.failed', { error })
      })
    }

    const envelope = {
      type: 'event',
      action,
      payload,
      meta: {
        ts: Date.now(),
        client: this.clientId,
        ...this.baseMeta,
        ...meta
      }
    }

    const serialized = JSON.stringify(envelope)
    const sent = this.socket.send(serialized)
    this.diagnostics.info('event.sent', {
      action,
      queued: !sent,
      payloadSize: serialized.length
    })
  }

  /**
   * 发送命令：自动挂载 requestId/元信息，返回 Promise
   */
  async sendCommand(action, payload = {}, options = {}) {
    const { timeout = this.commandTimeout, meta, requestId = generateRequestId(), signal, resendOnReconnect = this.resendOnReconnect } = options

    if (this.autoConnect && !this.socket.isConnected()) {
      this.socket.connect().catch((error) => {
        this.diagnostics.error('connect.failed', { error })
      })
    }

    const envelope = {
      type: 'command',
      action,
      requestId,
      payload,
      meta: {
        ts: Date.now(),
        client: this.clientId,
        ...this.baseMeta,
        ...meta
      }
    }

    const serialized = JSON.stringify(envelope)

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        const abortError = new CommandError({
          message: '命令已取消',
          code: 'command.aborted',
          action,
          requestId,
          data: payload,
          meta: envelope.meta,
          raw: envelope
        })
        reject(abortError)
        return
      }

      const context = {
        action,
        requestId,
        payload,
        envelope,
        serialized,
        resolve,
        reject,
        timeout,
        createdAt: Date.now(),
        signal,
        resendOnReconnect
      }

      if (timeout > 0) {
        context.timer = setTimeout(() => {
          this.pendingCommands.delete(requestId)
          const commandError = new CommandError({
            message: '服务器响应超时，请稍后重试',
            code: 'command.timeout',
            action,
            requestId,
            data: payload,
            meta: envelope.meta,
            raw: envelope
          })
          this.diagnostics.warn('command.timeout', { action, requestId, timeout })
          reject(commandError)
        }, timeout)
      }

      if (signal) {
        context.abortHandler = () => {
          this._finalizeCommand(requestId)
          const commandError = new CommandError({
            message: '命令已取消',
            code: 'command.aborted',
            action,
            requestId,
            data: payload,
            meta: envelope.meta,
            raw: envelope
          })
          this.diagnostics.warn('command.abort', { action, requestId })
          reject(commandError)
        }
        signal.addEventListener('abort', context.abortHandler, { once: true })
      }

      this.pendingCommands.set(requestId, context)

      const sent = this.socket.send(serialized)
      this.diagnostics.info('command.sent', {
        action,
        requestId,
        queued: !sent,
        payloadSize: serialized.length
      })
    })
  }

  /**
   * 统一处理 socket 消息入口
   */
  async _handleSocketMessage(event) {
    const { data } = event
    if (typeof data !== 'string') {
      this.diagnostics.warn('message.nonString', { data })
      return
    }
    let message
    try {
      message = JSON.parse(data)
    } catch (error) {
      this.diagnostics.error('message.parseError', { error, data })
      return
    }

    this.rawHandlers.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        this.diagnostics.error('raw.handlerError', { error })
      }
    })

    if (!message || typeof message !== 'object') {
      this.diagnostics.warn('message.invalidStructure', { message })
      return
    }

    const { type } = message
    switch (type) {
      case 'commandResult':
        await this._handleCommandResult(message)
        break
      case 'event':
        await this._handleEvent(message)
        break
      default:
        this.diagnostics.warn('message.unknownType', { type })
    }
  }

  /**
   * 命令响应处理：resolve/reject 挂起的 Promise
   */
  async _handleCommandResult(message) {
    const { requestId, status } = message
    if (!requestId) {
      this.diagnostics.warn('commandResult.missingRequestId', { message })
      return
    }
    const context = this.pendingCommands.get(requestId)
    if (!context) {
      this.diagnostics.warn('commandResult.unknownRequest', { requestId })
      return
    }

    this._finalizeCommand(requestId)

    if (status === 'success') {
      this.diagnostics.info('command.success', { action: context.action, requestId })
      context.resolve(message.data)
      return
    }

    if (status !== 'error') {
      this.diagnostics.warn('commandResult.unknownStatus', { status })
      context.resolve(message.data)
      return
    }

    this.diagnostics.warn('command.failed', {
      action: context.action,
      requestId,
      code: message.code,
      message: message.message
    })

    context.reject(new CommondResultError(message))
  }

  async _handleEvent(message) {
    const { action, payload } = message
    if (!action) {
      this.diagnostics.warn('event.missingAction', { message })
      return
    }

    this.diagnostics.debug('event.received', { action })

    const handlers = this.eventHandlers.get(action)
    let handled = false
    if (handlers && handlers.size > 0) {
      handlers.forEach((handler) => {
        try {
          const result = handler(payload, message)
          if (result === true) {
            handled = true
          }
        } catch (error) {
          this.diagnostics.error('event.handlerError', { error, action })
        }
      })
    }

    if (!handled) {
      this.diagnostics.warn('event.unhandled', { message })
    }
  }

  _handleSocketClose() {
    this.diagnostics.warn('socket.closed')
  }

  _handleSocketReconnect() {
    this.diagnostics.info('socket.reconnect')
    if (!this.resendOnReconnect) return
    this.pendingCommands.forEach((context, requestId) => {
      if (!context.resendOnReconnect) return
      this.diagnostics.info('command.resend', { action: context.action, requestId })
      this.socket.send(context.serialized)
    })
  }

  /**
   * 完成命令：清理定时器、abort 监听与上下文
   */
  _finalizeCommand(requestId) {
    const context = this.pendingCommands.get(requestId)
    if (!context) return
    if (context.timer) {
      clearTimeout(context.timer)
    }
    if (context.signal && context.abortHandler) {
      context.signal.removeEventListener('abort', context.abortHandler)
    }
    this.pendingCommands.delete(requestId)
  }
}

const messageHub = new MessageHub({ socket: socketClient, diagnostics: diagnosticsManager.createChannel('messageHub') })

export { MessageHub, messageHub }
export default messageHub
